import { Label } from '../../../components/ui/label';
import { Badge } from '../../../components/ui/badge';
import { Textarea } from '../../../components/ui/textarea';
import { Loader2, FileQuestion } from 'lucide-react';
import type { SupplierFormData } from './types';

interface WizardStep5RiskProps {
  formData: SupplierFormData;
  onRiskAnswerChange: (questionId: string, value: string) => void;
  previewMode: boolean;
  riskQuestions: Array<{
    id: string;
    question: string;
    type: string;
    options: string[];
    scores?: Record<string, number>;
    max_score?: number;
  }>;
  loadingRiskQuestions: boolean;
}

export default function WizardStep5Risk({
  formData,
  onRiskAnswerChange,
  previewMode,
  riskQuestions,
  loadingRiskQuestions,
}: WizardStep5RiskProps) {
  const completedAnswers = Object.keys(formData.risk_answers).length;

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-xl font-semibold mb-2">Approval stage</h2>
        <p className="text-muted-foreground">
          Complete the risk assessment questionnaire.
        </p>
      </div>

      {loadingRiskQuestions ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground mr-2" />
          <span className="text-muted-foreground">Loading risk questions...</span>
        </div>
      ) : riskQuestions.length === 0 ? (
        <div className="text-center py-12 bg-gray-50 rounded-lg border-2 border-dashed">
          <FileQuestion className="h-12 w-12 mx-auto text-gray-400 mb-4" />
          <p className="text-muted-foreground mb-2">No risk questions configured for this customer.</p>
          <p className="text-xs text-muted-foreground">Risk questions can be set up in the Onboarding Datasets configuration.</p>
        </div>
      ) : (
      <>
      <div className="space-y-8">
        {riskQuestions.map((question, index) => (
          <div key={question.id} className="space-y-3">
            <Label className="text-base font-medium">
              <span className="font-bold">{index + 1}.</span>{' '}
              {question.question}
            </Label>
            {question.type === 'text' ? (
              <div className="ml-5">
                <Textarea
                  placeholder="Enter your response..."
                  value={formData.risk_answers[question.id] || ''}
                  onChange={(e) => onRiskAnswerChange(question.id, e.target.value)}
                  disabled={previewMode}
                  rows={3}
                  className="resize-none"
                />
              </div>
            ) : (
              <div className="flex flex-col gap-2 ml-5">
                {question.options.map((option) => {
                  const isSelected = formData.risk_answers[question.id] === option;
                  const score = question.scores?.[option];
                  return (
                    <label key={option} className="flex items-center gap-3 cursor-pointer">
                      <input
                        type="radio"
                        name={`risk_${question.id}`}
                        value={option}
                        checked={isSelected}
                        onChange={() => onRiskAnswerChange(question.id, option)}
                        disabled={previewMode}
                        className="w-4 h-4 text-blue-600 border-gray-300 focus:ring-blue-500"
                      />
                      <span className="text-sm">{option}</span>
                      {isSelected && score !== undefined && (
                        <Badge variant="outline" className={`text-[10px] px-1.5 py-0 ${
                          score <= 3 ? 'border-green-300 text-green-700' :
                          score <= 6 ? 'border-yellow-300 text-yellow-700' :
                          'border-red-300 text-red-700'
                        }`}>
                          {score}/{question.max_score || 10}
                        </Badge>
                      )}
                    </label>
                  );
                })}
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Spend Score + Risk Level summary */}
      {completedAnswers > 0 && (
        <div className="flex items-center gap-6 mt-6 pt-4 border-t">
          {/* Spend Score badge — only shown when annual spend was selected in Step 2 */}
          {formData.annual_spend ? (
            <Badge className="bg-green-600 text-white text-sm px-4 py-1.5 hover:bg-green-600">
              Spend Score:{' '}
              {(() => {
                const spendScores: Record<string, number> = {
                  under_10k: 2, '10k_50k': 4, '50k_100k': 5,
                  '100k_500k': 6, '500k_1m': 8, over_1m: 10,
                };
                return spendScores[formData.annual_spend] || 0;
              })()}
            </Badge>
          ) : (
            <Badge variant="outline" className="text-sm px-4 py-1.5 text-muted-foreground">
              Spend Score: Not set (select in Step 2)
            </Badge>
          )}

          {/* Risk Score — calculated from per-option scores */}
          {(() => {
            const scoredQuestions = riskQuestions.filter(q => q.scores && formData.risk_answers[q.id]);
            if (scoredQuestions.length === 0) return null;
            const totalScore = scoredQuestions.reduce((sum, q) => {
              const answer = formData.risk_answers[q.id];
              return sum + (q.scores?.[answer] || 0);
            }, 0);
            const maxPossible = scoredQuestions.reduce((sum, q) => sum + (q.max_score || 10), 0);
            const riskPct = maxPossible > 0 ? Math.round((totalScore / maxPossible) * 100) : 0;

            let riskLabel = 'Low';
            let riskColor = 'bg-green-500';
            let badgeColor = 'bg-green-600';
            if (riskPct >= 66) { riskLabel = 'High'; riskColor = 'bg-red-500'; badgeColor = 'bg-red-600'; }
            else if (riskPct >= 33) { riskLabel = 'Medium'; riskColor = 'bg-yellow-500'; badgeColor = 'bg-orange-500'; }

            return (
              <>
                <Badge className={`${badgeColor} text-white text-sm px-4 py-1.5 hover:${badgeColor}`}>
                  Risk Score: {totalScore}/{maxPossible} ({riskPct}%)
                </Badge>
                <div className="flex items-center gap-2">
                  <span className="text-sm font-medium text-muted-foreground">Risk Level:</span>
                  <span className="inline-flex items-center gap-1.5">
                    <span className={`w-3 h-3 rounded-full ${riskColor}`} />
                    <span className="text-sm font-semibold">{riskLabel}</span>
                  </span>
                </div>
              </>
            );
          })()}
        </div>
      )}
      </>
      )}
    </div>
  );
}
